<script lang="ts">
  import classnames from 'classnames';

  export let variant: 'light' | 'dark' = 'dark';
  export let taller = false;
  export let darkOverlay = false;
  export let elementOffset = false;
  export let longContent = false;

  interface Data {
    image: string;
    srcset: string;
    sizes?: string;
    imageAlt: string;
    subtitle: string;
    title: string;
    text?: string;
  }

  const classNames = classnames($$props.class, {
    'hero-standard': true,
    'v-light': variant === 'light',
    'v-dark': variant === 'dark',
    'with-dark-overlay': darkOverlay,
    'element-offset': elementOffset,
    'long-content': longContent,
    taller: taller,
  });

  export let data: Data = {
    image: '/placeholders/mobile/hero-checkout-final-step.webp',
    srcset: '/placeholders/desktop/hero-checkout-final-step.webp 1x, /placeholders/high/hero-checkout-final-step.webp 2x',
    sizes: '',
    imageAlt: 'Alternate Image Text',
    subtitle: 'Subtitle',
    title: "Here's the title of the hero",
    text: ""
  };
</script>

<section class={classNames}>
  <div class="hero-grid">
    <div class="hero-image-wrapper">
      <!-- Image Specs -->
      <!-- Max width: 1440px -->
      <!-- Ratio: 2:1 (Mobile) Free (Desktop) -->
      {#if data.srcset && data.sizes }
        <img
          src={data.image}
          srcset={data.srcset}
          sizes={data.sizes}
          alt={data.imageAlt}
          class="hero-image"
        />
      {:else}
        <img
          src={data.image}
          alt={data.imageAlt}
          class="hero-image"
        />
      {/if}
    </div>
    <div class="hero-content">
      <p class="hero-standard-subtitle">
        {data.subtitle}
      </p>
      <h1 class="hero-standard-title">
        {data.title}
      </h1>

      {#if data.text} 
        <p class="hero-standard-text">
          {data.text}
        </p>
      {/if}

      {#if $$slots['action']}
        <div class="hero-action">
          <slot name="action" />
        </div>
      {/if}
    </div>
  </div>
</section>

<style type="text/scss" lang="scss" scoped>
  .hero-standard {
    --hero-height: 400px;
    @apply bg-transparent;
    @apply relative;

    &.v-dark {
      .hero-standard-subtitle {
        @apply md:text-white md:text-opacity-70;
      }
      .hero-standard-title {
        @apply md:text-white;
      }

      .hero-standard-text {
        @apply md:text-white;
      }
    }

    &.taller {
      --hero-height: 600px;
    }
  }

  .hero-grid {
    @apply grid grid-cols-default;
    @apply gap-x-grid-default;
    @apply w-full;
    @apply md:page-margin md:page-max-width;
    @apply z-3;

    @screen md {
      height: var(--hero-height);
    }

    .element-offset & {
      padding-bottom: var(--hero-element-grid-offset);
    }
  }

  .hero-standard-subtitle {
    @apply uppercase tracking-wider font-semibold;
    @apply text-sm md:text-base;
    @apply text-gray-600;
  }

  .hero-standard-title {
    @apply text-2xl md:text-6xl leading-snug md:leading-tighter;
    @apply font-medium font-serif;
    @apply text-gray-900;
  }
  

  .hero-standard-subtitle + .hero-standard-title {
    @apply mt-3;
  }

  .hero-standard-title + .hero-action {
    @apply mt-5;
  }

  .hero-content {
    @apply relative;
    @apply col-span-full;
    @apply page-margin py-6 md:p-0;
    @apply md:row-start-1 md:col-start-1 md:col-span-7 lg:col-start-1 lg:col-span-5;
    @apply md:self-center;

    .long-content & {
      @apply md:row-start-1 md:col-start-1 md:col-span-7 lg:col-start-1 lg:col-span-6;
    }
  }

  .hero-image-wrapper {
    @apply md:-page-margin;
    @apply relative;
    @apply col-span-full;
    @apply md:row-start-1;

    /** Fix aspect ratio plugin */
    --tw-aspect-w: 2;
    --tw-aspect-h: 1;
    padding-bottom: calc(var(--tw-aspect-h) / var(--tw-aspect-w) * 100%);

    > * {
      @apply absolute w-full h-full;
      @apply inset-0;
    }

    @screen md {
      /* Remove aspect ratio */
      padding-bottom: 0;
    }

    /* Bottom gradient in the mobile version */
    &:before {
      content: '';
      @apply md:hidden;
      @apply bg-gradient-to-t from-black to-transparent opacity-10;
      @apply absolute inset-x-0 bottom-0;
      @apply h-[60px];
      @apply z-1;
    }

    /*  Dark Overlay for the brighter images */
    .v-dark.with-dark-overlay &::after {
      content: '';
      @apply hidden md:block;
      @apply absolute inset-0;
      @apply bg-gray-600 opacity-90;
      @apply lg:bg-gradient-to-r lg:from-gray-600 lg:to-gray-300 lg:opacity-100;
      @apply mix-blend-multiply;
    }
  }

  .hero-image {
    @apply object-cover object-center;
    @screen md {
      width: 100%;
      height: var(--hero-height);
    }
  }
</style>
